import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { newsletterAPI, uploadAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { mockTemplates } from '../data/mockData';
import { formatDate, getDaysUntilReset } from '../lib/utils';
import {
  FileText,
  Plus,
  Download,
  Clock,
  LogOut,
  Settings,
  Newspaper,
  Lock,
  Unlock,
  Trash2,
  Loader2,
  Upload,
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, newsletterLimit, logout, refreshLimit } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [isLoadingNewsletters, setIsLoadingNewsletters] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
  const templateUploadRef = useRef(null);

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const fetchNewsletters = async () => {
    try {
      const data = await newsletterAPI.list();
      setNewsletters(data);
    } catch (error) {
      console.error('Failed to fetch newsletters:', error);
    } finally {
      setIsLoadingNewsletters(false);
    }
  };

  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!newsletterLimit?.can_create) {
      alert('Monthly newsletter limit reached. Please wait for the limit to reset.');
      return;
    }

    setIsUploadingTemplate(true);
    try {
      // Navigate to custom editor with the uploaded file
      navigate('/editor/custom', { state: { uploadedFile: file } });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to process template');
    } finally {
      setIsUploadingTemplate(false);
      if (templateUploadRef.current) {
        templateUploadRef.current.value = '';
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCreateNewsletter = (templateId) => {
    if (!newsletterLimit?.can_create) {
      return;
    }
    navigate(`/editor/${templateId}`);
  };

  const handleEditNewsletter = (newsletterId) => {
    navigate(`/editor/edit/${newsletterId}`);
  };

  const handleDeleteNewsletter = async (newsletterId) => {
    if (!window.confirm('Are you sure you want to delete this newsletter?')) {
      return;
    }
    
    setDeletingId(newsletterId);
    try {
      await newsletterAPI.delete(newsletterId);
      setNewsletters(newsletters.filter(nl => nl.newsletter_id !== newsletterId));
      await refreshLimit();
    } catch (error) {
      console.error('Failed to delete newsletter:', error);
      alert('Failed to delete newsletter');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadPDF = async (newsletterId) => {
    setDownloadingId(newsletterId);
    try {
      const blob = await newsletterAPI.generatePDF(newsletterId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter_${newsletterId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Refresh newsletters to update status
      await fetchNewsletters();
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const daysUntilReset = newsletterLimit ? getDaysUntilReset(newsletterLimit.reset_date) : 0;
  const usagePercentage = newsletterLimit ? (newsletterLimit.used / newsletterLimit.limit) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-700 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-semibold text-gray-900">HOA Newsletter</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user?.name}
                  className="w-9 h-9 rounded-full"
                />
              )}
              <span className="text-sm font-medium text-gray-700 hidden sm:block">
                {user?.name}
              </span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome & Usage Section */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {/* Welcome Card */}
          <Card className="lg:col-span-2 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white border-0">
            <CardContent className="p-8">
              <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1>
              <p className="text-emerald-100 mb-6">
                Create professional newsletters for your HOA community. Choose a template and start designing.
              </p>
              <Button
                onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-emerald-700 hover:bg-emerald-50"
                disabled={!newsletterLimit?.can_create}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Newsletter
              </Button>
            </CardContent>
          </Card>

          {/* Usage Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-700" />
                Monthly Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Newsletters Created</span>
                    <span className="font-semibold">
                      {newsletterLimit?.used ?? 0} / {newsletterLimit?.limit ?? 2}
                    </span>
                  </div>
                  <Progress value={usagePercentage} className="h-2" />
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{Math.max(0, daysUntilReset)} days</span> until limit resets
                </div>
                {!newsletterLimit?.can_create && (
                  <Badge variant="destructive" className="w-full justify-center py-1">
                    Monthly limit reached
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates Section */}
        <section id="templates" className="mb-10">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose a Template</h2>
            {/* Upload Custom Template Button */}
            <div>
              <input
                ref={templateUploadRef}
                type="file"
                accept=".pdf"
                onChange={handleTemplateUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => templateUploadRef.current?.click()}
                disabled={!newsletterLimit?.can_create || isUploadingTemplate}
              >
                {isUploadingTemplate ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload Custom PDF Template
              </Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockTemplates.filter(t => t.id !== 'custom').map((template) => (
              <Card
                key={template.id}
                className={`overflow-hidden cursor-pointer transition-all hover:shadow-lg ${
                  !newsletterLimit?.can_create ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-1'
                }`}
                onClick={() => handleCreateNewsletter(template.id)}
              >
                <div
                  className={`h-36 flex items-center justify-center ${
                    template.id === 'riverwood'
                      ? 'bg-emerald-700'
                      : template.id === 'sample1'
                      ? 'bg-amber-600'
                      : template.id === 'sample2'
                      ? 'bg-slate-700'
                      : 'bg-teal-600'
                  }`}
                >
                  <Newspaper className="w-14 h-14 text-white/80" />
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                      {template.name}
                    </h3>
                    {template.customizable ? (
                      <Unlock className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{template.description}</p>
                  <Badge
                    variant={template.customizable ? 'default' : 'secondary'}
                    className={`mt-3 text-xs ${
                      template.customizable
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                        : ''
                    }`}
                  >
                    {template.customizable ? 'Customizable' : 'Fixed Design'}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Recent Newsletters */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Newsletters</h2>
          {isLoadingNewsletters ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
            </div>
          ) : newsletters.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {newsletters.map((newsletter) => (
                <Card key={newsletter.newsletter_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-emerald-700" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {newsletter.status}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{newsletter.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Created {formatDate(newsletter.created_at)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEditNewsletter(newsletter.newsletter_id)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                        onClick={() => handleDownloadPDF(newsletter.newsletter_id)}
                        disabled={downloadingId === newsletter.newsletter_id}
                      >
                        {downloadingId === newsletter.newsletter_id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteNewsletter(newsletter.newsletter_id)}
                        disabled={deletingId === newsletter.newsletter_id}
                      >
                        {deletingId === newsletter.newsletter_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Newspaper className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No newsletters yet</h3>
                <p className="text-gray-500 mb-4">Create your first newsletter to get started</p>
                <Button
                  onClick={() => document.getElementById('templates')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  disabled={!newsletterLimit?.can_create}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Newsletter
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
